import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Brightness4, Brightness7 } from "@mui/icons-material";
import { GlobalStyles, IconButton, Tooltip } from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";

import { getTheme } from "@/layout/themes";

export type ColorMode = "light" | "dark";

const COLOR_MODE_STORAGE_KEY = "rubi-color-mode";

const ColorModeContext = createContext<{
  mode: ColorMode;
  toggleMode: () => void;
}>({
  mode: "light",
  toggleMode: () => {},
});

const readStoredMode = (): ColorMode => {
  try {
    return window.localStorage.getItem(COLOR_MODE_STORAGE_KEY) === "dark"
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
};

const applyColorScheme = (mode: ColorMode) => {
  document.documentElement.dataset.colorScheme = mode;
};

interface ColorModeProviderProps {
  children: React.ReactNode;
}

export const ColorModeProvider = ({ children }: ColorModeProviderProps) => {
  const [mode, setMode] = useState<ColorMode>(() => {
    const initial = readStoredMode();
    applyColorScheme(initial);
    return initial;
  });

  const theme = useMemo(() => getTheme(mode), [mode]);

  useEffect(() => {
    applyColorScheme(mode);

    try {
      window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
    } catch {
      // ignore quota / private-mode failures
    }
  }, [mode]);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {/* Page background for every route. CssBaseline only runs inside
            MainLayout and LoginWrapper, so standalone pages (change/set
            password, add/verify phone…) otherwise stay white in dark mode. */}
        <GlobalStyles
          styles={(t) => ({
            body: {
              backgroundColor: t.palette.background.default,
              color: t.palette.text.primary,
            },
          })}
        />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);

export const ColorModeToggle: React.FC = () => {
  const theme = useTheme();
  const { toggleMode } = useColorMode();
  const isDark = theme.palette.mode === "dark";

  return (
    <Tooltip title={isDark ? "Light mode" : "Dark mode"}>
      <IconButton
        color="inherit"
        onClick={toggleMode}
        aria-label="Toggle color mode"
      >
        {isDark ? <Brightness7 /> : <Brightness4 />}
      </IconButton>
    </Tooltip>
  );
};
